import { useTitle } from "@/hooks/use-title.hook.ts";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import { NotificationSettingComponent } from "@/pages/me/components/NotificationSettingComponent.tsx";
import useNotificationsSettingService from "@/pages/me/notifications-setting-service.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  useNotificationsSetting,
  useNotificationsSettingSave,
} from "@/queries/use-employee-settings.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiNotificationChannelType, NotificationSettings } from "@/types/notification.types.ts";
import {
  Box,
  ButtonIcon,
  EmptyStateBase,
  FontAwesomeIcon,
  ListItem,
  Page,
  Skeleton,
  Stack,
  Title,
  useToasts,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { useNavigate } from "react-router-dom";

export const AccountSettingsNotifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showInfo } = useToasts();
  const { data: employeeMe } = useEmployeeMe();
  const { getNotificationsSettingVisibleToEmployee, mapNotificationChannelsToItems } =
    useNotificationsSettingService();
  useTitle(t("core:notification_settings"));

  const { data: notificationsSetting, isLoading } = useNotificationsSetting();
  const notificationsSettingSave = useNotificationsSettingSave();

  const [settingValues, setSettingValues] = useState<NotificationSettings | undefined>(
    notificationsSetting?.notifications_setting
  );
  const [syncedNotificationsSetting, setSyncedNotificationsSetting] =
    useState(notificationsSetting);

  if (notificationsSetting !== syncedNotificationsSetting) {
    setSyncedNotificationsSetting(notificationsSetting);
    if (notificationsSetting?.notifications_setting) {
      setSettingValues(notificationsSetting.notifications_setting);
    }
  }

  const isReady = !!employeeMe;
  const notificationsSettingMenu = useMemo(() => {
    if (!employeeMe) return [];
    return getNotificationsSettingVisibleToEmployee(employeeMe);
  }, [employeeMe]);

  const handleSettingChange = (
    key: keyof NotificationSettings,
    newValues: ApiNotificationChannelType[]
  ) => {
    const previousValues = settingValues;
    if (!previousValues) return;

    const nextValues = { ...previousValues, [key]: newValues };
    setSettingValues(nextValues);

    notificationsSettingSave.mutate(
      { notifications_setting: nextValues },
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.notificationsSetting.edited);
          showInfo({ title: t("common:changes_saved_successfully") });
        },
        onError: () => {
          setSettingValues((current) => (current === nextValues ? previousValues : current));
        },
      }
    );
  };

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.notificationsSetting.viewed);
  });

  return (
    <Page>
      <Page.Header
        backSlot={
          <ButtonIcon
            variant={"secondary"}
            icon={<FontAwesomeIcon icon={"fa-light fa-arrow-left"} />}
            onClick={() => navigate("/account-settings")}
          />
        }
        title={t("common:notifications")}
      />
      <Page.Content>
        <Box className={"container-sm mx-auto"}>
          <DemoUnavailable
            appearance="notice"
            label={t("integrations:demo_slack_notifications_title")}
            description={t("integrations:demo_slack_notifications_description")}
            className="mb-4"
          />
          <DemoUnavailable
            appearance="notice"
            label={t("integrations:demo_email_delivery_title")}
            description={t("integrations:demo_email_delivery_description")}
            className="mb-4"
          />
          {notificationsSettingMenu.length ? (
            <Stack gap={"sm"}>
              {notificationsSettingMenu.map((section, sectionIndex) => (
                <Stack className={"card-panel-sm"} key={`section-${sectionIndex}`} gap={"2xl"}>
                  <Title header={section.title} />
                  <Stack>
                    {section.items.map((item, itemIndex) => (
                      <ListItem
                        key={`item-${itemIndex}`}
                        title={item.title}
                        caption={item.description}
                        valueSlot={
                          settingValues ? (
                            <Box className={"pl-2"}>
                              <NotificationSettingComponent
                                values={settingValues[item.key] || []}
                                options={mapNotificationChannelsToItems(item.availableOptions)}
                                showCaption={false}
                                selectedEmptyValue={
                                  item.isInApp ? t("core:in_app") : t("common:off")
                                }
                                onChange={(newValues) => {
                                  handleSettingChange(
                                    item.key,
                                    newValues as ApiNotificationChannelType[]
                                  );
                                }}
                              />
                            </Box>
                          ) : (
                            isLoading && <Skeleton className={"w-20 h-5"} />
                          )
                        }
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : !isReady ? (
            <Stack className={"pt-8"} gap={"xl"}>
              <Box>
                <Skeleton className={"w-20 h-6 mb-4"}></Skeleton>
                <Skeleton className={"w-auto h-12 mb-4"}></Skeleton>
                <Skeleton className={"w-auto h-12 mb-4"}></Skeleton>
                <Skeleton className={"w-auto h-12"}></Skeleton>
              </Box>
            </Stack>
          ) : (
            <EmptyStateBase
              emoji={":turtle:"}
              title={t("core:notification_settings_no_available")}
              description={t("core:notification_settings_no_available_description")}
            />
          )}
        </Box>
      </Page.Content>
    </Page>
  );
};
